import { useCallback, useEffect, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useSubscribedMutation } from '@setvi/shared/hooks';
import { useMutation } from '@tanstack/react-query';
import type { Recipient } from '@setvi/shared/interfaces';
import {
  appendQuoteShareEmailsMutation,
  shareQuoteV2Mutation,
  ShareQuoteV2Response,
  showQuoteShareInSearchMutation
} from 'Services/Mutations/QuotesV2';
import { QuotesV2QueryKey } from 'Services/Query/QuotesV2';
import { useComposeEmail } from 'providersV2/compose-email/context';
import { buildQuoteShareUrl } from '../utils/share-url';

const PLACEHOLDER = '<p>Generating share link…</p>';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const collectEmails = (recipients: Recipient[]): string[] =>
  Array.from(
    new Set(
      recipients
        .map(r => r?.Email?.trim().toLowerCase())
        .filter((email): email is string =>
          Boolean(email && EMAIL_REGEX.test(email))
        )
    )
  );

export const useQuoteShareActions = () => {
  const { open: openComposeEmail, values: composeValues } = useComposeEmail();

  const composeBodyRef = useRef(composeValues.body);
  useEffect(() => {
    composeBodyRef.current = composeValues.body;
  }, [composeValues.body]);

  const pendingShareIdRef = useRef<string | null>(null);

  const { mutateAsync: shareQuote } = useSubscribedMutation(
    shareQuoteV2Mutation(),
    [
      QuotesV2QueryKey.GET_QUOTE,
      QuotesV2QueryKey.GET_QUOTES,
      QuotesV2QueryKey.GET_SUMMARY_STATS,
      QuotesV2QueryKey.GET_QUOTE_SHARES,
      QuotesV2QueryKey.GET_SHARES
    ]
  );

  // Use a plain useMutation here — invalidation isn't required for this
  // background tracking call, and we don't want a failure snackbar.
  const { mutateAsync: appendShareEmails } = useMutation(
    appendQuoteShareEmailsMutation()
  );

  // Show-in-search promotes the share from preview-only to searchable.
  // Refetch share analytics so the Outbox + Share Analytics tabs pick up
  // the newly visible row.
  const { mutateAsync: showShareInSearch } = useSubscribedMutation(
    showQuoteShareInSearchMutation(),
    [QuotesV2QueryKey.GET_SHARES, QuotesV2QueryKey.GET_QUOTE_SHARES]
  );

  const sendEmailWithLink = useCallback(
    async (quoteId: string) => {
      pendingShareIdRef.current = null;
      const previewUrlRef = { current: '' };
      const trackedUrlRef = { current: '' };

      openComposeEmail({
        subject: 'Shared quote',
        emailBody: PLACEHOLDER,
        // Swap the untracked preview link for the real tracking link the
        // moment before the email is actually sent. Drafts (including
        // mistaken pre-send clicks on the link inside the editor) stay
        // untracked.
        beforeSend: bodyHtml => {
          if (!previewUrlRef.current || !trackedUrlRef.current) return bodyHtml;
          return bodyHtml
            .split(previewUrlRef.current)
            .join(trackedUrlRef.current);
        },
        afterSend: recipients => {
          const shareId = pendingShareIdRef.current;
          pendingShareIdRef.current = null;
          if (!shareId) return;
          // Promote the share from preview-only to searchable now that
          // it's actually been sent.
          showShareInSearch({ shareId }).catch(() => {
            // Best-effort: the email has already been queued.
          });
          const emails = collectEmails(recipients);
          if (!emails.length) return;
          appendShareEmails({ shareId, emails }).catch(() => {
            // Best-effort: the email has already been queued.
          });
        }
      });

      const replacePlaceholder = (replacement: string) => {
        const current = composeBodyRef.current || PLACEHOLDER;
        const next = current.includes(PLACEHOLDER)
          ? current.replace(PLACEHOLDER, replacement)
          : `${replacement}${current}`;
        openComposeEmail({ emailBody: next }, true);
      };

      try {
        const response = (await shareQuote({
          quoteId
        })) as ShareQuoteV2Response;
        const trackedUrl = buildQuoteShareUrl(response?.urlPath || '');
        const previewUrl = buildQuoteShareUrl(response?.urlPath || '', {
          preview: true
        });
        if (!trackedUrl || !previewUrl) {
          replacePlaceholder('');
          enqueueSnackbar('Failed to generate share link. Please try again.', {
            variant: 'error'
          });
          return;
        }
        pendingShareIdRef.current = response?.shareId || null;
        previewUrlRef.current = previewUrl;
        trackedUrlRef.current = trackedUrl;
        replacePlaceholder(
          `<p>Hello,</p><p><br></p><p>Here is your <a href="${previewUrl}" target="_blank" rel="noopener noreferrer">Quote Link</a></p>`
        );
      } catch {
        replacePlaceholder('');
        enqueueSnackbar('Failed to generate share link. Please try again.', {
          variant: 'error'
        });
      }
    },
    [shareQuote, appendShareEmails, showShareInSearch, openComposeEmail]
  );

  return { sendEmailWithLink };
};
